import { useEffect, useMemo, useState } from "react";
import { Search, ShoppingCart } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/store/ProductCard";
import { CartSheet } from "@/components/store/CartSheet";
import { ComboDialog } from "@/components/store/ComboDialog";

export default function Store() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [category, setCategory] = useState("Todos");
  const [query, setQuery] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [combo, setCombo] = useState(null);
  const { count, subtotal } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const categories = useMemo(() => ["Todos", ...new Set(products.map((p) => p.category))], [products]);
  const visible = products.filter(
    (p) => (category === "Todos" || p.category === category) && p.name.toLowerCase().includes(query.toLowerCase())
  );
  const open = settings?.store_open ?? true;
  const combos = products.filter((p) => p.category === "Combos");

  return (
    <div className="min-h-screen pb-28">
      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Depósito do Primo" data-testid="store-logo" className="h-10 w-auto rounded-md md:h-12" />
          </div>
          <button data-testid="header-cart-button" onClick={() => setCartOpen(true)} className="relative grid h-10 w-10 place-items-center rounded-full bg-white/5 hover:bg-white/10">
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <motion.span key={count} initial={{ scale: 1.4 }} animate={{ scale: 1 }} data-testid="cart-count-badge"
                className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primo-500 px-1 text-[11px] font-bold text-white">{count}</motion.span>
            )}
          </button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 pt-5">
        <div className="grain relative overflow-hidden rounded-3xl border border-white/[0.08]">
          <img src="/images/logo.png" alt="Depósito do Primo" data-testid="hero-logo" className="pointer-events-none absolute right-0 top-1/2 hidden h-[80%] w-auto max-w-[50%] -translate-y-1/2 object-contain opacity-60 sm:block" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
          <div className="relative max-w-lg px-6 py-10 sm:px-10 sm:py-14 rise">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Peça em segundos</p>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95]">
              Bebida gelada <span className="text-primo-400">na sua porta.</span>
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
              <span data-testid="store-status" className={`rounded-full border px-3 py-1 font-semibold ${open ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-red-500/40 bg-red-500/10 text-red-400"}`}>
                {open ? "Aberto agora" : "Fechado no momento"}
              </span>
              {settings && <span data-testid="store-delivery-fee" className="text-slate-300">Entrega: {brl(settings.delivery_fee)} · Retirada grátis</span>}
              {settings?.min_order_delivery > 0 && <span className="text-slate-300">Pedido mínimo p/ entrega: {brl(settings.min_order_delivery)}</span>}
            </div>
            <div data-testid="store-hours" className="mt-4 space-y-0.5 text-sm text-slate-300">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Horário de funcionamento</p>
              <p>Segunda a sexta: <span className="font-semibold text-white">8h às 20h</span></p>
              <p>Sábado: <span className="font-semibold text-white">8h às 00h</span></p>
              <p>Domingo: <span className="font-semibold text-white">8h às 14h</span></p>
            </div>
          </div>
        </div>
      </section>

      <section className="sticky top-[61px] z-20 mx-auto max-w-6xl bg-[#0A0A0A]/90 px-4 py-4 backdrop-blur-xl">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="product-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar bebida..."
            className="w-full rounded-full border border-white/[0.08] bg-[#1A1A1A] py-2.5 pl-10 pr-4 text-sm outline-none focus:border-primo-500/60" />
        </div>
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          {categories.map((c) => (
            <button key={c} data-testid={`category-pill-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setCategory(c)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${category === c ? "bg-primo-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4">
        {combos.length > 0 && category === "Todos" && !query && (
          <section data-testid="combos-showcase" className="mb-8">
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Promoção</p>
                <h2 className="font-display text-base font-bold md:text-lg">Combos que valem a pena</h2>
              </div>
              <button data-testid="see-all-combos" onClick={() => setCategory("Combos")} className="text-xs font-semibold text-primo-400 hover:text-primo-300">Ver todos</button>
            </div>
            <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-1">
              {combos.map((p, i) => (
                <div key={p.id} className="w-44 shrink-0 sm:w-56">
                  <ProductCard product={p} index={i} onConfigure={setCombo} />
                </div>
              ))}
            </div>
          </section>
        )}
        <div data-testid="product-grid" className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
          {visible.map((p, i) => <ProductCard key={p.id} product={p} index={i} onConfigure={setCombo} />)}
        </div>
        {visible.length === 0 && <p data-testid="no-products" className="py-16 text-center text-slate-500">Nenhum produto encontrado.</p>}
      </main>

      {count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 p-4">
          <button data-testid="floating-cart-button" onClick={() => setCartOpen(true)}
            className="mx-auto flex w-full max-w-md items-center justify-between rounded-full bg-primo-500 px-6 py-4 font-display font-bold text-white shadow-[0_10px_40px_rgba(255,90,31,0.35)] active:scale-[0.98] transition-transform">
            <span>Ver carrinho ({count})</span>
            <span>{brl(subtotal)}</span>
          </button>
        </div>
      )}

      <footer className="mx-auto max-w-6xl px-4 pt-16 text-xs text-slate-600">
        <a href="/admin" data-testid="admin-link" className="hover:text-slate-400">Área do administrador</a>
      </footer>

      <CartSheet open={cartOpen} onOpenChange={setCartOpen} storeOpen={open} />
      <ComboDialog open={!!combo} onOpenChange={(o) => !o && setCombo(null)} product={combo} />
    </div>
  );
}
